/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import { ArrowDownRightIcon } from '@/components/icons';
import { Link } from '@/components/link';
import { Region } from '@/components/region';
import ArticleTile from '@/components/article-tile';
import { BreadcrumbSection } from '@/components/breadcrumb-section';
import { cn } from '@/lib/utils';
import { type DefaultSettingsValue } from '@/lib/page-designer/default-settings';
import type { LearnFolder, LearnListing as LearnListingData } from '@/lib/learn/catalog.server';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

/** Build the `/learn` URL for a folder path (`''` → the Learn root). */
function learnHref(path: string): string {
    return path ? `/learn/${path}` : '/learn';
}

function Breadcrumb({ crumbs }: { crumbs: LearnFolder[] }): ReactElement {
    return (
        <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center text-sm leading-5 text-foreground">
                <li className="flex items-center">
                    {crumbs.length === 0 ? (
                        <span className="font-medium">Learn</span>
                    ) : (
                        <Link to={learnHref('')} className="hover:underline">
                            Learn
                        </Link>
                    )}
                </li>
                {crumbs.map((crumb, i) => {
                    const isLast = i === crumbs.length - 1;
                    return (
                        <li key={crumb.path} className="flex items-center">
                            <ChevronRight className="mx-1 size-3" aria-hidden />
                            {isLast ? (
                                <span className="font-medium">{crumb.name}</span>
                            ) : (
                                <Link to={learnHref(crumb.path)} className="hover:underline">
                                    {crumb.name}
                                </Link>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

/**
 * Auto-generated Learn listing — breadcrumb, child categories, and an article-tile grid for one view
 * of the `/learn` taxonomy (root or folder), from the cached `learn-taxonomy` catalog. When a
 * merchandiser has filed an **Article Listing** Page Designer page in this folder (UCR-2756), `page`
 * is that page and its regions render around the grid: `learnTopFullWidth` (full-bleed, flush to the
 * header), `learnTopContent` (above the grid), `learnBottom` (after the grid). No page → the default
 * listing renders unchanged. See `src/lib/learn/catalog.server.ts`.
 */
export default function LearnListing({
    listing,
    page,
}: {
    listing: LearnListingData;
    page?: PageWithComponentData | null;
}): ReactElement {
    const { folder, subfolders, articles, breadcrumb } = listing;

    // Page-level settings from the Article Listing page (the page analog of a component's `data`):
    //  - theme: defaults to light; a merchandiser opts into dark (like the PLP grid section, UCR-2819).
    //    Uses `bm-theme-dark` (not the PLP's raw `bg-bm-black`) so the ArticleTiles flip to a legible
    //    light palette via the `.bm-theme-dark .article-tile` rules (article-tile.css, UCR-2765) — a raw
    //    black background would make the ink tiles blend into the section and disappear.
    //  - title: an optional merchandiser heading that replaces the default folder / 'Learn' title.
    const pageData = page?.data as { settings?: DefaultSettingsValue; title?: string } | undefined;
    const dark = pageData?.settings?.themeColor === 'dark';
    const heading = pageData?.title?.trim() || folder?.name || 'Learn';

    return (
        // When authored, `-mt-8` pulls the full-width region flush to the header (mirrors PLP/home).
        // No bottom padding — the last section's own padding is the only gap before the footer.
        <div className={cn(page && '-mt-8')}>
            {page ? <Region page={page} regionId="learnTopFullWidth" fallbackOnEmpty errorElement={null} /> : null}

            {/* Top Content Region — full-window width (not capped) so a dark Page Designer component
                bleeds edge-to-edge; components self-constrain their interior via their own
                section-container (mirrors the PLP/home/article full-bleed region pattern). */}
            {page ? <Region page={page} regionId="learnTopContent" fallbackOnEmpty errorElement={null} /> : null}

            {/* Article-grid section — optional dark (black) background per the page's Default Settings.
                The theme wrapper is full-bleed so the color spans the viewport; the content-width child
                constrains the grid to the page width. Light (default) leaves the page background. */}
            <div className={cn(dark && 'bm-theme-dark')} data-slot="learn-grid-section">
                <div className="mx-auto w-full max-w-content-full px-4 py-8">
                    <BreadcrumbSection>
                        <Breadcrumb crumbs={breadcrumb} />
                    </BreadcrumbSection>
                    {/* Match the PLP grid title: type-h3 + trailing down-right arrow. */}
                    <div className="mb-6 flex items-end gap-3">
                        <h1 className="type-h3">{heading}</h1>
                        <ArrowDownRightIcon
                            aria-hidden
                            className="size-[18px] shrink-0 md:size-[27px]"
                            data-slot="learn-title-arrow"
                        />
                    </div>

                    {subfolders.length > 0 ? (
                        <ul className="mb-8 flex flex-wrap gap-2">
                            {subfolders.map((sub) => (
                                <li key={sub.path}>
                                    <Link
                                        to={learnHref(sub.path)}
                                        className={cn(
                                            'inline-block rounded-full border px-4 py-1.5 text-sm',
                                            dark
                                                ? 'border-current/30 hover:bg-current/10'
                                                : 'border-border hover:bg-muted'
                                        )}>
                                        {sub.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    ) : null}

                    {articles.length > 0 ? (
                        // `auto-rows-fr` + `h-full` give every tile in the grid the same height,
                        // not just the siblings within one row: the rows all size to the tallest
                        // card, and each tile fills its cell. (UCR-3027)
                        <ul className="grid auto-rows-fr grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
                            {articles.map((article) => (
                                <li key={article.id}>
                                    <ArticleTile article={article} layout="stacked" className="h-full" />
                                </li>
                            ))}
                        </ul>
                    ) : subfolders.length === 0 ? (
                        <p className={cn(dark ? 'text-current/70' : 'text-muted-foreground')}>No articles yet.</p>
                    ) : null}
                </div>
            </div>

            {/* Bottom Region — full-window width, same full-bleed rationale as the Top Content Region. */}
            {page ? <Region page={page} regionId="learnBottom" fallbackOnEmpty errorElement={null} /> : null}
        </div>
    );
}
